import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  uploadImportFile,
  getImportJobStatus,
  getReviewItems,
  resolveReviewItem,
  dismissReviewItem,
} from "../../../services/crmImport";
import type {
  ImportJobStatusResponse,
  ImportReviewItemResponse,
} from "../../../types/crmImport";
import "./CrmImportPage.css";

type PageState = "idle" | "uploading" | "polling" | "review" | "done";

const POLL_INTERVAL_MS = 2000;

export const CrmImportPage = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pageState, setPageState] = useState<PageState>("idle");
  const [error, setError] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ImportJobStatusResponse | null>(null);
  const [reviewItems, setReviewItems] = useState<ImportReviewItemResponse[]>([]);
  const [actionItemId, setActionItemId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const processFile = async (file: File) => {
    setError("");
    setPageState("uploading");

    try {
      const { jobId: newJobId } = await uploadImportFile(file);
      setJobId(newJobId);
      setPageState("polling");
      pollJobStatus(newJobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
      setPageState("idle");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const pollJobStatus = (id: string) => {
    const poll = async () => {
      try {
        const status = await getImportJobStatus(id);
        setJobStatus(status);

        if (status.status === "pending" || status.status === "processing") {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else if (status.status === "completed" || status.status === "needs_review") {
          if (status.pendingReviewCount > 0) {
            const { items } = await getReviewItems(id);
            setReviewItems(items.filter((i) => i.status === "Pending"));
            setPageState("review");
          } else {
            setPageState("done");
          }
        } else {
          setPageState("done");
        }
      } catch {
        setError("Failed to check import status.");
        setPageState("done");
      }
    };

    poll();
  };

  const removeReviewItem = (itemId: string) => {
    setReviewItems((prev) => {
      const next = prev.filter((i) => i.id !== itemId);
      if (next.length === 0) setPageState("done");
      return next;
    });
  };

  const handleResolve = async (item: ImportReviewItemResponse) => {
    if (!jobId || !item.suggestedSchool) return;
    setActionItemId(item.id);
    try {
      await resolveReviewItem(jobId, item.id, item.suggestedSchool.id);
      removeReviewItem(item.id);
    } catch {
      setError("Failed to resolve item.");
    } finally {
      setActionItemId(null);
    }
  };

  const handleDismiss = async (itemId: string) => {
    if (!jobId) return;
    setActionItemId(itemId);
    try {
      await dismissReviewItem(jobId, itemId);
      removeReviewItem(itemId);
    } catch {
      setError("Failed to dismiss item.");
    } finally {
      setActionItemId(null);
    }
  };

  const handleReset = () => {
    setPageState("idle");
    setJobId(null);
    setJobStatus(null);
    setReviewItems([]);
    setError("");
  };

  return (
    <div className="crm-import">
      <Link to="/crm/contacts" className="crm-import__back">← Contacts</Link>

      {pageState === "idle" && (
        <div className="crm-import__header">
          <h1 className="crm-import__title">Import Schools & Coaches</h1>
          <p className="crm-import__subtitle">
            Upload an Excel file (.xlsx or .xls) containing school and coach data.
            Up to 5,000 rows per file, max 10 MB.
          </p>
        </div>
      )}

      {error && (
        <div className="crm-import__error" role="alert">
          {error}
        </div>
      )}

      {pageState === "idle" && (
        <div
          className={`crm-import__upload-area${dragging ? " crm-import__upload-area--dragging" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <label className="crm-import__upload-label" htmlFor="import-file">
            <span className="crm-import__upload-icon">📂</span>
            <span className="crm-import__upload-text">
              {dragging ? "Drop file here" : "Drag & drop or choose file"}
            </span>
            <span className="crm-import__upload-hint">.xlsx or .xls</span>
          </label>
          <input
            id="import-file"
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="crm-import__file-input"
            onChange={handleFileChange}
          />
        </div>
      )}

      {pageState === "uploading" && (
        <div className="crm-import__status">
          <div className="crm-import__spinner" />
          <p>Uploading file…</p>
        </div>
      )}

      {pageState === "polling" && jobStatus && (
        <div className="crm-import__status">
          <div className="crm-import__spinner" />
          <p>
            Processing <strong>{jobStatus.fileName}</strong> — {jobStatus.totalRows ?? 0} rows…
          </p>
        </div>
      )}

      {pageState === "polling" && !jobStatus && (
        <div className="crm-import__status">
          <div className="crm-import__spinner" />
          <p>Processing…</p>
        </div>
      )}

      {pageState === "review" && reviewItems.length > 0 && (
        <div className="crm-import__review">
          <h2 className="crm-import__review-title">
            Review Unmatched Schools ({reviewItems.length})
          </h2>
          <p className="crm-import__review-subtitle">
            These rows had school names that didn't match exactly. Accept the
            suggestion or dismiss the row.
          </p>
          <div className="crm-import__review-list">
            {reviewItems.map((item) => (
              <div key={item.id} className="crm-import__review-item">
                <div className="crm-import__review-item-info">
                  <span className="crm-import__review-row">Row {item.rowNumber}</span>
                  <span className="crm-import__review-school-in-file">
                    "{item.schoolNameInFile}"
                  </span>
                  {item.suggestedSchool ? (
                    <span className="crm-import__review-suggestion">
                      Suggested: <strong>{item.suggestedSchool.name}</strong>
                      {item.suggestedSchool.state ? ` · ${item.suggestedSchool.state}` : ""}
                      {item.confidenceScore != null
                        ? ` (${Math.round(item.confidenceScore * 100)}% match)`
                        : ""}
                    </span>
                  ) : (
                    <span className="crm-import__review-no-suggestion">
                      No school match found
                    </span>
                  )}
                </div>
                <div className="crm-import__review-item-actions">
                  {item.suggestedSchool && (
                    <button
                      className="crm-import__btn crm-import__btn--accept"
                      disabled={actionItemId === item.id}
                      onClick={() => handleResolve(item)}
                    >
                      Accept
                    </button>
                  )}
                  <button
                    className="crm-import__btn crm-import__btn--dismiss"
                    disabled={actionItemId === item.id}
                    onClick={() => handleDismiss(item.id)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pageState === "done" && jobStatus && (
        <div className="crm-import__done">
          <div className="crm-import__done-summary">
            <h2 className="crm-import__done-title">
              {jobStatus.status === "failed" ? "Import Failed" : "Import Complete"}
            </h2>
            <p className="crm-import__done-file">{jobStatus.fileName}</p>
            <div className="crm-import__done-stats">
              <div className="crm-import__stat">
                <span className="crm-import__stat-value">{jobStatus.totalRows ?? 0}</span>
                <span className="crm-import__stat-label">Total Rows</span>
              </div>
              <div className="crm-import__stat crm-import__stat--success">
                <span className="crm-import__stat-value">{jobStatus.successfulRows ?? 0}</span>
                <span className="crm-import__stat-label">Imported</span>
              </div>
              <div className="crm-import__stat crm-import__stat--failed">
                <span className="crm-import__stat-value">{jobStatus.failedRows ?? 0}</span>
                <span className="crm-import__stat-label">Failed</span>
              </div>
            </div>
            {jobStatus.errorLog && (
              <details className="crm-import__error-log">
                <summary>Error details</summary>
                <pre>{jobStatus.errorLog}</pre>
              </details>
            )}
          </div>
          <button className="crm-import__btn crm-import__btn--primary" onClick={handleReset}>
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
};
